import { useContext, useMemo } from "react";
import { TABLE_SELECTION_MODE } from "const";
import { amountRoundOff } from "helpers/NumberFormatHelper";
import styles from "./styles.module.css";
import CloseIcon from "images/svg/Close.svg";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import ShopContext from "context/ShopContext";
import SalesContext from "context/SalesContext";

export default function TableOrdersDialog({
  open,
  onClose,
  onSelectSale,
  onNewOrder,
  table,
  selectionMode = TABLE_SELECTION_MODE.SINGLE,
  selectedSales = [],
  onToggleSale,
  filteredSales = null,
  title = null,
}) {
  const { selectedShop } = useContext(ShopContext);
  const { sales } = useContext(SalesContext);

  const tableSales = useMemo(() => {
    if (filteredSales) {
      const ids = new Set(filteredSales.map((s) => s.local_id || s.id));
      return sales.filter(
        (sale) => ids.has(sale.local_id || sale.id) && Number(sale?.table?.id) === Number(table?.id)
      );
    }
    return sales.filter((sale) => Number(sale?.table?.id) === Number(table?.id));
  }, [filteredSales, sales, table?.id]);

  if (!open) return null;

  const handleSaleClick = (sale) => {
    if (selectionMode === TABLE_SELECTION_MODE.MULTI) {
      onToggleSale?.(sale);
    } else {
      onSelectSale(sale);
    }
  };

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        {/* Header Section */}
        <div className={styles.headInfoSec}>
          <h2>{title || `${table?.name} - Orders`}</h2>
          <img src={CloseIcon} alt="Close" onClick={onClose} className={styles.icon} />
        </div>

        {/* Content Section */}
        <div className={styles.contentArea}>
          {tableSales && tableSales.length > 0 ? (
            <div className={styles.salesList}>
              {tableSales.map((sale, index) => {
                const isSelected = selectedSales.some((s) => String(s.local_id) === String(sale.local_id));
                return (
                  <div
                    key={sale.id || index}
                    className={`${styles.saleRow} ${isSelected ? styles.selectedRow : ""}`}
                    onClick={() => handleSaleClick(sale)}
                  >
                    {selectionMode === TABLE_SELECTION_MODE.MULTI && (
                      <div className={styles.checkboxWrapper}>
                        <input type="checkbox" checked={isSelected} readOnly />
                      </div>
                    )}
                    <div className={styles.saleContent}>
                      <div className={styles.saleMainInfo}>
                        <span className={styles.customerName}>
                          {sale.customer ? sale.customer.name : `Order #${sale.local_id}`}
                          {sale.table?.chair?.name && (
                            <span className={styles.chairTag}> • {sale.table.chair.name}</span>
                          )}
                        </span>
                        <span className={styles.statusText}>{sale.status}</span>
                        <span className={styles.amount}>
                          <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                          {amountRoundOff(sale.payable || 0)}
                        </span>
                      </div>
                      <div className={styles.saleSubInfo}>
                        <span>{DateTimeHelpers.formatDateTime(sale.time)}</span>
                        <span>{sale.items ? sale.items.length : 0} items</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyState}>No active orders for this table.</div>
          )}
        </div>

        {/* Action Buttons */}
        <div className={styles.buttonSection}>
          <ButtonGray onClick={onClose}>{selectionMode === TABLE_SELECTION_MODE.MULTI ? "Done" : "Cancel"}</ButtonGray>
          {selectionMode !== TABLE_SELECTION_MODE.MULTI && <ButtonRed onClick={onNewOrder}>Start New Order</ButtonRed>}
        </div>
      </div>
    </div>
  );
}
